import { useForm } from '@mantine/form'
import { IconLogin } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { zodResolver } from 'mantine-form-zod-resolver'
import { Text, Title, Paper, Button, Center, Divider, Box } from '@mantine/core'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import { useStoreBase } from '@store/index'

import { SignInSchema, SignInType, UserType } from '@customTypes/user'

import { UI } from '@constants/app'
import { RoutesApp } from '@constants/routes'

import { useIsMobile } from '@hooks/mobile'

import { ErrorService } from '@utils/error'
import { isLoadingMutation } from '@utils/network'

import Input from '@components/shared/input'
import { useDisclosure } from '@mantine/hooks'
import ForgotPassword from '@components/login/forgotPassword'

export default function Login() {
  const navigate = useNavigate()

  const isMobile = useIsMobile()

  const { setUser, reset } = useStoreBase()

  const [openModal, { close, open }] = useDisclosure()

  const form = useForm<SignInType>({
    initialValues: {
      email: '',
      password: '',
    },
    validate: zodResolver(SignInSchema),
  })

  const siginMutation = useMutation<UserType, ErrorService, SignInType>({
    mutationFn: async (params) => {
      const response = await DataRepo.userService.signinWithEmailAndPassword(params)
      queryClient.clear()
      reset()
      return response
    },
    onSettled: (userData, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message || 'Error desconocido',
        })
      }
      if (userData) {
        setUser(userData)
        navigate(RoutesApp.HOME)
      } else {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: 'No se obtuvo usuario',
        })
      }
    },
  })

  const Container = isMobile ? Box : Paper

  return (
    <Center className="cd-w-full cd-h-full">
      <Container
        withBorder
        className="cd-min-w-full cd-p-[1.25rem] md:cd-min-w-[450px] md:cd-p-[2rem]"
        radius="md"
        shadow="xs"
      >
        <form onSubmit={form.onSubmit((values) => siginMutation.mutate(values))}>
          <div className="cd-flex cd-flex-col cd-gap-[2rem]">
            <Title className="cd-title-form" order={2}>
              Sein
              <Text inherit c="blue" component="span">
                pro
              </Text>
            </Title>

            <div className="cd-flex cd-flex-col cd-gap-[1rem]">
              <Input
                capitalize={false}
                label="Email"
                placeholder="email@seinpro.ec"
                type="email"
                typeInput="text"
                {...form.getInputProps('email')}
                onChange={(e) =>
                  form.setFieldValue('email', e.target.value.toLowerCase().trim(), {
                    forceUpdate: true,
                  })
                }
              />

              <Input
                capitalize={false}
                label="Contraseña"
                placeholder="&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;"
                size={UI.Size}
                type="password"
                typeInput="text"
                {...form.getInputProps('password')}
              />

              <Button
                fullWidth
                className="cd-mt-[0.5rem]"
                leftSection={<IconLogin />}
                loaderProps={{ type: 'dots' }}
                loading={isLoadingMutation(siginMutation)}
                size={UI.Size}
                type="submit"
                variant="filled"
              >
                Iniciar sesión
              </Button>
            </div>

            <Divider label="o" />

            <div className="cd-flex cd-flex-col cd-gap-y-[1rem]">
              <Link to={RoutesApp.REGISTER}>
                <Text className="cd-text-sm cd-text-center">
                  ¿No tienes cuenta?{' '}
                  <Text inherit c="blue" component="span">
                    Regístrate
                  </Text>
                </Text>
              </Link>
              <Text className="cd-text-sm cd-text-center">
                ¿Olvidaste tu contraseña?{' '}
                <Text
                  inherit
                  c="blue"
                  className="cd-cursor-pointer"
                  component="span"
                  onClick={open}
                >
                  Recupérala
                </Text>
              </Text>
            </div>
          </div>
        </form>
        <ForgotPassword modal data={form.values.email} open={openModal} size="md" onClose={close} />
      </Container>
    </Center>
  )
}
